import { Box } from "@mui/material";
import ActionCard from "app/frontend/components/ActionCard";
import { useContactSubmissionProjectParams } from "app/frontend/components/contact/useContactSubmissionProjectParams";
import { useIsEligibilityReadOnly } from "app/frontend/components/contact/useIsEligibilityReadOnly";
import BaseEligibilityAccordion from "app/frontend/components/eligibility/accordions/BaseEligibilityAccordion";
import { IEligibilityProps } from "app/frontend/components/eligibility/accordions/types";
import { EEligibilityStep } from "app/frontend/components/eligibility/constants";
import { useOnContinue } from "app/frontend/components/eligibility/hooks/useOnContinue";
import {
    EIncentiveName,
    EProjectAccessibility,
    PROJECT_ACCESSIBILITY_NAME_BY_ENUM,
} from "app/frontend/constants/enums";
import { useProjectParams } from "app/frontend/hooks/usePropsParams";
import { toId } from "app/frontend/utils/inputs";
import { useCallback } from "react";

import MixedIcon from "app/frontend/assets/images/mhd/mixed.svg?react";
import PrivateIcon from "app/frontend/assets/images/mhd/private.svg?react";
import PublicIcon from "app/frontend/assets/images/mhd/public.svg?react";
import UnsureIcon from "app/frontend/assets/images/mhd/unsure.svg?react";

const AboutYourProjectAccordion: React.FC<IEligibilityProps> = ({ head, edit }) => {
    const onContinue = useOnContinue();
    const contactParams = useContactSubmissionProjectParams();
    const readOnly = useIsEligibilityReadOnly();

    const projectParams = useProjectParams();
    const isPublic = (projectParams ?? contactParams)?.accessibility === EProjectAccessibility.PUBLICLY_ACCESSIBLE;

    const getForceNextHead = useCallback(() => {
        if (isPublic && head > EEligibilityStep.VOUCHERS && edit === EEligibilityStep.ABOUT) {
            return EEligibilityStep.VOUCHERS;
        } else {
            return undefined;
        }
    }, [edit, head, isPublic]);

    return (
        <BaseEligibilityAccordion
            header={
                projectParams.accessibility
                    ? PROJECT_ACCESSIBILITY_NAME_BY_ENUM[projectParams.accessibility]
                    : "Your Site"
            }
            h2="Tell us about your Medium- Heavy-Duty Project"
            step={EEligibilityStep.ABOUT}
        >
            <Box
                sx={{
                    display: "flex",
                    flexWrap: { xs: "wrap", lg: "nowrap" },
                    flexDirection: "row",
                    justifyContent: "center",
                    gap: "20px",
                }}
            >
                {CARDS.map((card) => {
                    return (
                        <ActionCard
                            key={toId(card.title)}
                            card={{
                                ...card,
                                disabled: readOnly,
                                sx: {
                                    ...card.sx,
                                    paddingTop: "30px",
                                    border:
                                        readOnly && card.accessibility === contactParams?.project?.accessibility
                                            ? "3px solid"
                                            : undefined,
                                    borderColor:
                                        readOnly && card.accessibility === contactParams?.project?.accessibility
                                            ? "primary.light"
                                            : undefined,
                                },
                                action: {
                                    text: "",
                                    onClick: () => {
                                        // If users select this publicly accessible card, they should be taken to the "Where Is Your Project Located" page,
                                        // skipping the Voucher Incentive Program page
                                        // https://www.figma.com/design/7rTRsZRfuKDdypoCwzHzld/ConEdison-Sharable?node-id=12142-12394&p=f&m=dev
                                        return card.accessibility === EProjectAccessibility.PUBLICLY_ACCESSIBLE
                                            ? onContinue({
                                                  forceNextHead: EEligibilityStep.VOUCHERS + 1,
                                                  newProjectParams: {
                                                      incentive_names: [EIncentiveName.PUBLICLY_ACCESSIBLE],
                                                      accessibility: card.accessibility,
                                                      voucher_names: [],
                                                  },
                                              })
                                            : onContinue({
                                                  forceNextHead: getForceNextHead(),
                                                  newProjectParams: {
                                                      incentive_names: [EProjectAccessibility.PRIVATELY_ACCESSIBLE],
                                                      accessibility: card.accessibility,
                                                  },
                                              });
                                    },
                                },
                            }}
                        />
                    );
                })}
            </Box>
        </BaseEligibilityAccordion>
    );
};

const CARDS = [
    {
        title: "Publicly Accessible",
        alt: "Publicly Accessible",
        body: "Site is open to all EV owners.",
        image: PublicIcon,
        imagePosition: "center" as const,
        imageFit: "contain" as const,
        accessibility: EProjectAccessibility.PUBLICLY_ACCESSIBLE,
        sx: {
            border: "none",
            boxShadow: "none",
        },
    },
    {
        title: "Privately Accessible",
        alt: "Privately Accessible",
        body: "Site is closed to public access.",
        image: PrivateIcon,
        imagePosition: "center" as const,
        imageFit: "contain" as const,
        accessibility: EProjectAccessibility.PRIVATELY_ACCESSIBLE,
        sx: {
            border: "none",
            boxShadow: "none",
        },
    },
    {
        title: "Shared/Mixed Accessibility",
        alt: "Shared/Mixed Accessibility",
        body: "A portion of the site is open to the public.",
        image: MixedIcon,
        imagePosition: "center" as const,
        imageFit: "contain" as const,
        accessibility: EProjectAccessibility.MIXED_ACCESSIBILITY,
        sx: {
            border: "none",
            boxShadow: "none",
        },
    },
    {
        title: "Not Sure Yet",
        alt: "Not Sure Yet",
        body: "I'm still working through the details.",
        image: UnsureIcon,
        imagePosition: "center" as const,
        imageFit: "contain" as const,
        accessibility: EProjectAccessibility.NOT_SURE_ACCESSIBILITY,
        sx: {
            border: "none",
            boxShadow: "none",
        },
    },
];

export default AboutYourProjectAccordion;
